import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

/**
 * Two versions, deliberately kept apart.
 *
 *  APP    — the native shell the player installed from a store or an APK.
 *           Android versionName / iOS CFBundleShortVersionString, plus a build
 *           number (Android versionCode / iOS CFBundleVersion) that uniquely
 *           identifies one CI run. Only changes when the player installs a new
 *           native build.
 *
 *  BUNDLE — the JS that shell is currently RUNNING. Ships inside the native
 *           build, then gets replaced over the air (see services/otaBoot), so
 *           it moves independently and is the version that actually determines
 *           what code a player is executing.
 *
 * They match on a fresh install of a coordinated release and diverge the
 * moment an OTA update lands. Reporting one number for "the version" is how
 * you end up debugging the wrong code: a crash report saying 1.2.3 is
 * ambiguous between the shell and the JS, and it is almost always the JS you
 * want. Every surface below therefore reports BOTH, always labelled.
 */

/**
 * SOFTWARE version: the JS bundle currently running.
 *
 * The fallback is deliberately NOT a version-shaped string. It used to be
 * '999.999.999', which reads as a real version and hid a live bug: the
 * TestFlight workflow never set REACT_APP_VERSION, so every TestFlight build
 * reported '999.999.999' and nobody noticed. Now that this value also feeds
 * the Sentry release and the Pendo visitor, a plausible-looking fake would
 * quietly poison telemetry too. 'dev' cannot be mistaken for a build.
 */
export const BUNDLE_VERSION = process.env.REACT_APP_VERSION || 'dev';

export type AppVersions = {
  /**
   * Whether a native shell exists at all. Distinguishes "web, there is only
   * one version" from "native, the shell version has not been read yet" —
   * both of which leave nativeVersion null, but they label differently.
   */
  hasNativeShell: boolean;
  /** HARDWARE version, e.g. '1.2.3'. Null on web, where there is no shell. */
  nativeVersion: string | null;
  /** HARDWARE build number (Android versionCode / iOS CFBundleVersion). */
  nativeBuild: string | null;
  /** SOFTWARE version: the running JS bundle. */
  bundleVersion: string;
};

let cached: AppVersions = {
  hasNativeShell: Capacitor.isNativePlatform(),
  nativeVersion: null,
  nativeBuild: null,
  bundleVersion: BUNDLE_VERSION
};

/**
 * Synchronous snapshot. The native half is null until loadAppVersions() has
 * resolved (the Capacitor call is async), so render from this and let the
 * label collapse to bundle-only rather than blocking on it.
 */
export const getAppVersions = (): AppVersions => cached;

const readNativeVersions = async (): Promise<AppVersions> => {
  try {
    const info = await CapacitorApp.getInfo();
    cached = {
      hasNativeShell: true,
      nativeVersion: info.version,
      nativeBuild: info.build,
      bundleVersion: BUNDLE_VERSION
    };
  } catch (err) {
    // Leave the native half null; the bundle version is the one that matters
    // for identifying running code, and it never depends on this call.
    //
    // Logged rather than swallowed, because the silence is the problem: a
    // failed read here is indistinguishable from running on web, and it costs
    // the shell-version dimension on every Sentry event and Pendo visitor for
    // the whole session with nothing anywhere to say why.
    console.warn(
      '[version] native shell version unavailable; continuing on the bundle version',
      err
    );
  }
  return cached;
};

/**
 * The read in flight, so concurrent callers share one native call.
 *
 * Caching only the RESULT was not enough. Three consumers ask for this during
 * boot (useAppVersions, the Sentry tags, the Pendo identify) and they all run
 * before the first one resolves, so each entered getInfo() and the last
 * response to arrive won. Deliberately never cleared: a failed read leaves the
 * native half null and every later caller gets that same settled answer, which
 * is the behaviour the callers already handle. Retrying per call is what would
 * reintroduce the stampede.
 */
let inFlight: Promise<AppVersions> | undefined;

/**
 * Read the native shell version once and cache it. Safe to call repeatedly and
 * on web, where App.getInfo() is unimplemented and this resolves to the
 * bundle-only shape.
 */
export const loadAppVersions = (): Promise<AppVersions> => {
  if (!Capacitor.isNativePlatform() || cached.nativeVersion) return Promise.resolve(cached);
  inFlight = inFlight ?? readNativeVersions();
  return inFlight;
};

/**
 * Display label, worded for the platform it is on.
 *
 *   native: "Hardware 1.2.3" + "Client 1.4.0"
 *   web:    "Version 1.4.0"
 *
 * "Hardware" is the installed native shell, "Client" is the JS actually
 * running. Player-facing wording, chosen over the App/Bundle pair these used
 * to read: support asks players which version they are on, and "bundle" is
 * our word, not theirs.
 *
 * On web there is no shell and no OTA, so there is only one version and
 * calling it "Client" would imply a second one that does not exist. The
 * split exists to disambiguate two numbers; where there is only one, naming
 * it "Version" is the clearer answer.
 *
 * nativeBuild is deliberately NOT shown. It still travels with the telemetry
 * (see the Sentry appBuild tag and the Pendo visitor), where it separates two
 * builds sharing a marketing version, but on screen it is a third number
 * competing with the two that matter.
 *
 * Keyed off hasNativeShell rather than off whether nativeVersion has arrived,
 * so a native launch does not flash "Version 1.4.0" and then relabel itself
 * once the async read lands.
 */
export const formatVersionLines = (versions: AppVersions = cached): string[] => {
  if (!versions.hasNativeShell) return [`Version ${versions.bundleVersion}`];

  const lines: string[] = [];
  if (versions.nativeVersion) lines.push(`Hardware ${versions.nativeVersion}`);
  lines.push(`Client ${versions.bundleVersion}`);
  return lines;
};

/**
 * @deprecated Renders only the bundle version with an ambiguous "Version:"
 * label, which is exactly the confusion this module exists to remove. Use
 * useAppVersions() + formatVersionLines(). Kept so nothing breaks mid-migration.
 */
export const APP_VERSION = BUNDLE_VERSION;
